import { UUID } from 'crypto';
import { ErrorCode } from './configs/errorCode';
import { FormTemplate, Question, QuestionType } from '@/remoteApi/types/form-template.types';
import { ApiErrorMessage, Attachment, FormHandlerConfig, FormState } from './types';
import { FormAnswerMap } from './ui/productTypeForm/ProductTypeForm';
import moment from 'moment';
import { AppPaths } from '@/configs/appPaths';
import { Form, FormAnswer } from '@/remoteApi/FormApi';

export function getErrorContent(code: ErrorCode) {
	switch (code) {
		case ErrorCode.DUPLICATED_EMAIL:
			return 'Podany adres e-mail jest już zajęty.';
		case ErrorCode.INVALID_HASH:
			return 'Twój klucz resetu hasła jest niepoprawny lub wygasł. Powtórz proces resetu hasła.';
		case ErrorCode.INVALID_PASSWORD:
			return 'Obecne hasło jest niepoprawne.';
		default:
			return 'Coś poszło nie tak! Spróbuj ponownie później';
	}
}

export function getErrorMessage(codes: ApiErrorMessage) {
	const codeToMessage = (code: ErrorCode | string) =>
		Object.values(ErrorCode).includes(code as ErrorCode)
			? getErrorContent(code as ErrorCode)
			: '';

	const errorMessages = Array.isArray(codes)
		? codes.map((code) => codeToMessage(code))
		: [codeToMessage(codes)];

	const message =
		errorMessages.filter((m) => !!m).join('. ') ||
		codeToMessage(ErrorCode.GENERIC);

	return message;
}

export function getFileSrc(path: string) {
	return `${process.env.NEXT_PUBLIC_API_URL_UPLOADS}${path}`;
}

export function getImageProps(image: Attachment) {
	return {
		src: `${process.env.NEXT_PUBLIC_API_URL_UPLOADS}${image.path}`,
		height: image.height || 100,
		width: image.width || 100,
	};
}

export const getInitialAnswerData = (questionType: Question) => {
	return {
		answerId: null,
		value:
			questionType.questionType === QuestionType.DIMENSIONS
				? '100|100|100'
				: '',
		attachments: [],
		required: false,
	};
};

export function getInitialAnswersMapData(formTemplate: FormTemplate) {
	const data: [UUID, FormAnswerMap][] = [];

	for (const questionGroup of formTemplate.questionGroups) {
		let isAdvancedQuestion = false;

		for (const question of questionGroup.questions) {
			if (question.questionType === QuestionType.ADVANCED_OPTIONS) {
				isAdvancedQuestion = true;
				continue;
			}

			data.push([
				question.id,
				{
					...getInitialAnswerData(question),
					required:
						!isAdvancedQuestion && question.questionType !== QuestionType.FILES,
				},
			]);
		}
	}

	return data;
}

export function getAllAnswersIds(answersMap: Map<UUID, FormAnswerMap>) {
	return Array.from(answersMap.values()).map((answer) => answer.answerId);
}

export function formatDateForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('DD.MM.YYYY');
}

export function formatDateTimeForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('DD.MM.YYYY HH:mm');
}

export function calculateMinCustomDimensionProducts(
	width: number,
	height: number,
	depth: number,
	minTotalSurface: number
) {
	// minTotalSurface is in m2, other dimensions in mm
	const minTotalSurfaceInMm2 = minTotalSurface * 1_000_000; // mm2

	const minTotalSurfacePerProduct = (depth + width) * 2 * (depth + height);
	const minProducts = Math.ceil(
		minTotalSurfaceInMm2 / minTotalSurfacePerProduct
	);
	return minProducts;
}

export const formatPrice = (value: number, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'symbol',
		maximumFractionDigits: 2,
		...opts,
	});

	return formatter.format(value);
};

export const validatePassword = (password: string) => {
	const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{6,}$/;
	return passwordRegex.test(password);
};

export const validateEmail = (email: string) => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

export const validatePostalCode = (postalCode: string) => {
	const postalCodeRegex = /^\d{2}-\d{3}$/;
	return postalCodeRegex.test(postalCode);
};

export const getFullPathUrl = (path: AppPaths) => {
	const baseUrl = process.env.APP_URL;
	if (!baseUrl) {
		throw new Error('Base URL is not defined');
	}
	return `${baseUrl}${path}`;
};

export function createFormHandler<T extends Record<string, any>>(
	config: FormHandlerConfig<T>
) {
	return async function formAction(
		prevState: FormState<T>,
		formData: FormData
	) {
		try {
			const values = Object.fromEntries(
				Object.keys(config.fields).map((key) => [
					key,
					formData.get(key)?.toString().trim() || '',
				])
			) as T;

			for (const [key, fieldConfig] of Object.entries(config.fields)) {
				const value = values[key as keyof T];

				if (fieldConfig.required && !value) {
					const name = fieldConfig.label || key;
					return {
						values,
						error: `Pole ${name} jest wymagane`,
						message: '',
					};
				}

				if (fieldConfig.validations) {
					for (const validation of fieldConfig.validations) {
						const isValid = await validation.validate(value, values);
						if (!isValid) {
							return {
								values,
								error: validation.message,
								message: '',
							};
						}
					}
				}
			}

			await config.onSubmit(values);

			return {
				values: {} as T,
				message: config.successMessage,
				error: '',
			};
		} catch (e) {
			const error = e as Error;
			return {
				...prevState,
				error: error.message ?? 'Wystąpił nieznany błąd',
				message: '',
			};
		}
	};
}

export const mapFormAnswers = (form: Form) => {
	const answersMap: Record<string, Record<UUID, FormAnswer[]>> = {};
	const attachments: Attachment[] = [];

	form.formAnswers.forEach((answer) => {
		const { questionGroup, questionId } = answer.question;

		if (!answersMap[questionGroup]) {
			answersMap[questionGroup] = {};
		}

		if (!answersMap[questionGroup][questionId]) {
			answersMap[questionGroup][questionId] = [];
		}

		if (answer.files.length) {
			attachments.push(...answer.files);
		}

		answersMap[questionGroup][questionId].push(answer);
	});

	return { answersMap, attachments };
};
